/**
 * Les options d'export PDF, partagées par les deux points d'entrée.
 *
 * POURQUOI CE COMPOSANT EXISTE. L'atelier et l'édition d'une fiche avaient
 * chacun leur panneau. Celui de l'atelier ignorait la moitié des réglages et
 * son aperçu ne reflétait rien ; on ne s'en apercevait qu'en téléchargeant.
 * Deux interfaces pour un même geste finissent toujours par diverger : il n'y
 * en a plus qu'une.
 *
 * Ce composant ne sait RIEN de l'endroit d'où il est appelé. Il reçoit des
 * chargeurs — d'où viennent les listes, l'aperçu et le compte de pages — et
 * les valeurs courantes. C'est ce qui permet à une fiche enregistrée et à une
 * sélection en cours de composition de partager l'interface sans partager
 * leurs routes.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2, Lock } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

/** Ce qu'on affiche avant que le backend n'ait répondu. Il reste la seule
 *  source de vérité : ces listes évitent seulement une fenêtre vide. */
export const TYPES_PAR_DEFAUT = [
  { id: "exercices", nom: "Exercices", titre_par_defaut: "Exercices", duree: false, date_a_rendre: false },
  { id: "controle", nom: "Contrôle", titre_par_defaut: "CONTRÔLE", duree: true, date_a_rendre: false },
  { id: "dm", nom: "Devoir Maison", titre_par_defaut: "DEVOIR MAISON", duree: false, date_a_rendre: true },
];

export const DENSITES_PAR_DEFAUT = [
  { id: "compacte", nom: "Compact", resume: "économie de papier" },
  { id: "standard", nom: "Standard", resume: "équilibre" },
  { id: "confort", nom: "Confort", resume: "espace pour écrire" },
];

export const ADAPTATIONS_PAR_DEFAUT = [
  { id: "standard", nom: "Standard", resume: "aucun aménagement" },
  { id: "lisibilite", nom: "Lisibilité renforcée", resume: "texte plus grand, lignes plus courtes, figures agrandies" },
  { id: "concentration", nom: "Concentration", resume: "un exercice à la fois, sans décor" },
];

/** Le mot « template » ne paraît plus : ce qui reste est une identité
 *  visuelle. La mise en colonnes appartient à la densité. */
export const STYLES_VISUELS = [
  { id: "free_minimal", nom: "Sobre", pro: false },
  { id: "pro_classique", nom: "Classique", pro: true },
  { id: "pro_academique", nom: "Académique", pro: true },
  { id: "pro_moderne", nom: "Moderne", pro: true },
];

/** Les valeurs de départ, communes aux deux écrans. */
export const VALEURS_INITIALES = {
  documentType: "exercices",
  densite: "standard",
  adaptation: "standard",
  templateId: "free_minimal",
  customTitle: "",
  dueDate: "",
  duration: "",
  showBranding: true,
  corrige: false,
};

const A4_LARGEUR = 794;
const A4_HAUTEUR = 1123;

export default function OptionsExportPDF({
  valeurs,
  onChange,
  chargerOptions,
  chargerApercu,
  chargerPages,
  isPro = false,
  className = "",
}) {
  const [options, setOptions] = useState(null);
  const [avanceOuvert, setAvanceOuvert] = useState(false);

  const [apercuHtml, setApercuHtml] = useState("");
  const [apercuEnCours, setApercuEnCours] = useState(false);
  const [apercuErreur, setApercuErreur] = useState("");

  const [pages, setPages] = useState(null);
  const [pagesEnCours, setPagesEnCours] = useState(false);

  // La page fait 794 × 1123 px (A4 à 96 dpi). L'aperçu garde cette géométrie,
  // sinon il ment sur tout ce qui dépend de la hauteur de page.
  const boiteRef = useRef(null);
  const [echelle, setEchelle] = useState(1);

  const modifier = useCallback((patch) => onChange({ ...valeurs, ...patch }),
    [onChange, valeurs]);

  /* ---------------- les listes viennent du backend ---------------- */
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const recu = await chargerOptions();
        if (!annule) setOptions(recu || null);
      } catch (e) {
        if (!annule) setOptions(null);
      }
    })();
    return () => { annule = true; };
  }, [chargerOptions]);

  /* ---------------- l'aperçu, le vrai ---------------- */
  const signature = JSON.stringify(valeurs);
  useEffect(() => {
    let annule = false;
    const minuterie = setTimeout(async () => {
      setApercuEnCours(true);
      setApercuErreur("");
      try {
        const html = await chargerApercu(valeurs);
        if (!annule) setApercuHtml(html || "");
      } catch (e) {
        if (!annule) {
          setApercuHtml("");
          setApercuErreur("Aperçu indisponible. Les réglages restent appliqués au PDF.");
        }
      } finally {
        if (!annule) setApercuEnCours(false);
      }
    }, 350);
    return () => { annule = true; clearTimeout(minuterie); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, chargerApercu]);

  /* ---------------- le coût en papier ---------------- */
  useEffect(() => {
    if (!chargerPages) return undefined;
    let annule = false;
    // Trois mises en page complètes : on attend que le réglage soit posé.
    const minuterie = setTimeout(async () => {
      setPagesEnCours(true);
      try {
        const recu = await chargerPages(valeurs);
        if (!annule) setPages(recu || null);
      } catch (e) {
        // Le compte de pages est un confort : son absence n'empêche pas
        // d'exporter.
        if (!annule) setPages(null);
      } finally {
        if (!annule) setPagesEnCours(false);
      }
    }, 600);
    return () => { annule = true; clearTimeout(minuterie); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, chargerPages]);

  /* ---------------- l'échelle de la page ---------------- */
  useEffect(() => {
    const boite = boiteRef.current;
    if (!boite) return undefined;
    const ajuster = () => {
      const largeur = boite.clientWidth;
      if (largeur > 0) setEchelle(largeur / A4_LARGEUR);
    };
    ajuster();
    const observateur = new ResizeObserver(ajuster);
    observateur.observe(boite);
    return () => observateur.disconnect();
  }, [apercuHtml]);

  const types = options?.types || TYPES_PAR_DEFAUT;
  const densites = options?.densites || DENSITES_PAR_DEFAUT;
  const adaptations = options?.adaptations || ADAPTATIONS_PAR_DEFAUT;

  /* LES STYLES PRO SE MONTRENT, VERROUILLÉS ; ils ne se cachent plus.
   *
   * Le filtre précédent — `isPro || !s.pro` — les retirait de la liste. Un
   * compte gratuit ne voyait donc qu'un seul choix, « Sobre », et repartait
   * en croyant que le produit n'en propose qu'un. C'est le premier retour
   * d'usage qu'on a eu sur téléphone.
   *
   * L'accès n'est plus décidé ici : il vient de `is_accessible`, que le
   * backend calcule déjà par style selon l'offre. Le drapeau local ne sert
   * plus que de repli quand la route n'a pas répondu — la fenêtre s'ouvre
   * alors avec les styles connus, sans mentir sur ce qui est accessible. */
  const styles = STYLES_VISUELS.map((s) => {
    const distant = options?.styles?.find((x) => x.id === s.id);
    return { ...s, accessible: distant ? distant.is_accessible !== false
                                       : (isPro || !s.pro) };
  });
  const typeCourant = types.find((t) => t.id === valeurs.documentType);
  const pagesConnues = Object.fromEntries((pages || []).map((d) => [d.id, d.pages]));
  const colonnesBridees = (pages || []).some((d) => d.colonnes_bridees);

  const bouton = (choisi) =>
    `min-h-[44px] rounded-md border px-3 text-left transition-colors ${
      choisi ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"
    }`;

  return (
    <div className={`grid grid-cols-1 gap-6 lg:grid-cols-[1fr,1.05fr] ${className}`}>
      <div className="space-y-6">

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-gray-900">Quel document ?</legend>
          <div className="flex flex-wrap gap-2">
            {types.map((t) => (
              <button key={t.id} type="button" aria-pressed={valeurs.documentType === t.id}
                onClick={() => modifier({ documentType: t.id })}
                className={`${bouton(valeurs.documentType === t.id)} flex-1 text-center text-sm font-medium`}>
                {t.nom}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-gray-900">Combien de papier ?</legend>
          <div className="space-y-1.5">
            {densites.map((d) => (
              <button key={d.id} type="button" aria-pressed={valeurs.densite === d.id}
                onClick={() => modifier({ densite: d.id })}
                className={`${bouton(valeurs.densite === d.id)} flex w-full items-center justify-between gap-3 py-2`}>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">{d.nom}</span>
                  {d.resume && <span className="block text-xs text-gray-500">{d.resume}</span>}
                </span>
                <span className="shrink-0 text-sm tabular-nums text-gray-600">
                  {typeof pagesConnues[d.id] === "number"
                    ? `${pagesConnues[d.id]} page${pagesConnues[d.id] > 1 ? "s" : ""}`
                    : pagesEnCours ? "…" : ""}
                </span>
              </button>
            ))}
          </div>
          {colonnesBridees && (
            <p className="text-xs text-gray-500">
              L'aménagement choisi ne se met jamais sur deux colonnes : Compact
              gagne donc moins de pages qu'à l'ordinaire.
            </p>
          )}
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-gray-900">Un aménagement pour l'élève ?</legend>
          <div className="space-y-1.5">
            {adaptations.map((a) => (
              <button key={a.id} type="button" aria-pressed={valeurs.adaptation === a.id}
                onClick={() => modifier({ adaptation: a.id })}
                className={`${bouton(valeurs.adaptation === a.id)} block w-full py-2`}>
                <span className="block text-sm font-medium text-gray-900">{a.nom}</span>
                {a.resume && <span className="block text-xs text-gray-500">{a.resume}</span>}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="rounded-md border border-gray-200">
          <button type="button" onClick={() => setAvanceOuvert((v) => !v)}
            aria-expanded={avanceOuvert}
            className="flex min-h-[44px] w-full items-center justify-between px-3 text-sm font-medium text-gray-700">
            Personnaliser davantage
            <ChevronDown className={`h-4 w-4 transition-transform ${avanceOuvert ? "rotate-180" : ""}`} />
          </button>
          {avanceOuvert && (
            <div className="space-y-4 border-t border-gray-100 px-3 py-3">
              <div className="space-y-1">
                <Label htmlFor="titre-imprime" className="text-sm font-medium">Titre imprimé</Label>
                {/* 44 px, comme toutes les cibles depuis L6 : ces champs étaient
                    restés à 36. */}
                <Input id="titre-imprime" className="h-11" value={valeurs.customTitle}
                  placeholder={typeCourant?.titre_par_defaut || "Exercices"}
                  onChange={(e) => modifier({ customTitle: e.target.value })} />
              </div>

              {typeCourant?.duree && (
                <div className="space-y-1">
                  <Label htmlFor="duree" className="text-sm font-medium">Durée</Label>
                  <Input id="duree" className="h-11" value={valeurs.duration} placeholder="Ex : 1h00"
                    onChange={(e) => modifier({ duration: e.target.value })} />
                </div>
              )}

              {typeCourant?.date_a_rendre && (
                <div className="space-y-1">
                  <Label htmlFor="date-rendu" className="text-sm font-medium">À rendre le</Label>
                  <Input id="date-rendu" className="h-11" type="date" value={valeurs.dueDate}
                    onChange={(e) => modifier({ dueDate: e.target.value })} />
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-sm font-medium">Style visuel</Label>
                <div className="grid grid-cols-2 gap-2">
                  {styles.map((s) => (s.accessible ? (
                    <button key={s.id} type="button" aria-pressed={valeurs.templateId === s.id}
                      onClick={() => modifier({ templateId: s.id })}
                      className={`${bouton(valeurs.templateId === s.id)} text-center text-sm`}>
                      {s.nom}
                    </button>
                  ) : (
                    /* Verrouillé, pas cassé. Le libellé garde son contraste
                     * plein et la pastille dit pourquoi : un style grisé se
                     * lit comme une panne, un cadenas comme une offre. */
                    <button key={s.id} type="button" disabled aria-disabled="true"
                      title={`${s.nom} est réservé à l'offre Pro`}
                      className="flex min-h-[44px] cursor-not-allowed items-center
                                 justify-center gap-1.5 rounded-md border border-dashed
                                 border-purple-300 bg-purple-50/60 px-3 text-center text-sm
                                 text-foreground">
                      {s.nom}
                      <span className="inline-flex items-center gap-1 rounded-full
                                       bg-purple-100 px-1.5 py-0.5 text-[11px]
                                       font-semibold text-purple-700">
                        <Lock className="h-3 w-3" aria-hidden="true" />Pro
                      </span>
                    </button>
                  )))}
                </div>
                {styles.some((s) => !s.accessible) && (
                  <p className="pt-1 text-xs text-muted-foreground">
                    Les styles marqués « Pro » sont inclus dans l'offre Pro. Votre fiche
                    s'exporte normalement avec le style Sobre.
                  </p>
                )}
              </div>

              {isPro && (
                <div className="flex min-h-[44px] items-center justify-between gap-3">
                  <Label htmlFor="identite" className="text-sm font-medium">
                    Afficher l'identité de l'établissement
                  </Label>
                  <Switch id="identite" checked={valeurs.showBranding}
                    onCheckedChange={(v) => modifier({ showBranding: v })} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ---------------- l'aperçu ----------------
          SUR TÉLÉPHONE, IL PASSE EN TÊTE ET IL Y RESTE.
          Mesuré avant : la page A4 commençait à y=776 dans une fenêtre de
          776 px — zéro pixel visible sans défiler. On voyait le mot
          « Aperçu », jamais la chose, et juger l'effet d'une densité
          demandait un aller-retour de plus de 500 px à chaque essai.
          `order-first` le remonte, `sticky top-0` l'y maintient. Au-dessus de
          `lg`, la colonne de droite reprend sa place et son ampleur.
          La bande est réduite mais la boîte garde son défilement interne :
          on voit en permanence le haut du document, et le reste de la page
          reste atteignable en faisant défiler DANS la bande. Le rendu, lui,
          est le même — même fonction, mêmes paramètres, même fidélité au PDF. */}
      <div className="order-first flex min-w-0 flex-col gap-3 self-start sticky top-0 z-10 bg-card pb-2 lg:static lg:order-none lg:pb-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium">Aperçu</div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="min-h-[40px]"
              variant={!valeurs.corrige ? "default" : "outline"}
              onClick={() => modifier({ corrige: false })}>Sujet</Button>
            <Button size="sm" className="min-h-[40px]"
              variant={valeurs.corrige ? "default" : "outline"}
              onClick={() => modifier({ corrige: true })}>Corrigé</Button>
          </div>
        </div>
        <div ref={boiteRef}
          className="relative mt-3 max-h-[170px] overflow-y-auto overflow-x-hidden rounded-md border bg-white lg:mt-0 lg:max-h-[560px]">
          {apercuEnCours && (
            <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded bg-white/90 px-2 py-1 text-xs text-gray-600 shadow-sm">
              <Loader2 className="h-3 w-3 animate-spin" />
              Mise à jour…
            </div>
          )}
          {apercuErreur ? (
            <div className="p-4 text-sm text-amber-700">{apercuErreur}</div>
          ) : apercuHtml ? (
            <div style={{ width: "100%", height: A4_HAUTEUR * echelle, overflow: "hidden" }}>
              <iframe title="Aperçu du PDF" srcDoc={apercuHtml} sandbox=""
                style={{
                  width: A4_LARGEUR, height: A4_HAUTEUR, border: 0,
                  transform: `scale(${echelle})`, transformOrigin: "top left",
                }} />
            </div>
          ) : (
            <div className="flex h-[40vh] items-center justify-center text-sm text-gray-500 lg:h-[560px]">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Préparation de l'aperçu…
            </div>
          )}
        </div>
        {/* Masquée sur téléphone : dans la bande collante, elle coûterait
            60 px permanents. Sa promesse — regarder ne consomme rien — est
            reprise dans le pied de fenêtre, au plus près du bouton. */}
        <p className="hidden text-xs text-gray-500 lg:block">
          Cet aperçu est le document lui-même : mêmes réglages que le PDF que
          vous téléchargerez. Le regarder ne consomme aucune fiche.
        </p>
      </div>
    </div>
  );
}
